import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../../components/ProductCard/ProductCard';
import Buy3Banner from '../../components/Buy3Banner/Buy3Banner';
import { PRODUCTS } from '../../data/products.js';
import { FiGrid, FiList, FiFilter, FiChevronDown } from 'react-icons/fi';
import { CONFIG } from '../../config/config'; // ✅ FIXED IMPORT
import './Products.css';

const Products = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');

  const queryCategory = useMemo(
    () => new URLSearchParams(location.search).get('category') || '',
    [location.search]
  );

  const [filters, setFilters] = useState({
    category: queryCategory,
    priceRange: [0, CONFIG.PRICE.ORIGINAL],
    size: [],
    color: [],
  });

  const [showFilters, setShowFilters] = useState(false);

  const categories = useMemo(() => CONFIG.CATEGORIES, []);

  const maxPrice = useMemo(
    () =>
      Math.max(
        ...PRODUCTS.map((p) => Number(p.originalPrice || 0)),
        CONFIG.PRICE.ORIGINAL
      ),
    []
  );

  const products = useMemo(() => {
    const selectedCategory = queryCategory || filters.category;
    const selectedMeta = CONFIG.CATEGORIES.find(
      (c) => c.id === selectedCategory
    );

    if (selectedCategory && selectedMeta && !selectedMeta.active) return [];

    const filtered = PRODUCTS.filter((p) => {
      const passCategory =
        !selectedCategory || p.category === selectedCategory;

      const passPrice =
        Number(p.price) <= filters.priceRange[1];

      const passSize =
        !filters.size.length ||
        filters.size.some((s) => p.sizes?.includes(s));

      return passCategory && passPrice && passSize;
    });

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return a.id - b.id;
      }
    });
  }, [filters, queryCategory, sortBy]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      if (filterType === 'category') {
        return { ...prev, category: value };
      }

      if (filterType === 'size') {
        const newSizes = prev.size.includes(value)
          ? prev.size.filter((s) => s !== value)
          : [...prev.size, value];

        return { ...prev, size: newSizes };
      }

      return prev;
    });
  };

  const clearFilters = () => {
    navigate('/products');
    setFilters({
      category: '',
      priceRange: [0, maxPrice],
      size: [],
      color: [],
    });
  };

  const selectedCategory = queryCategory || filters.category;
  const selectedMeta = CONFIG.CATEGORIES.find(
    (c) => c.id === selectedCategory
  );

  const showComingSoon =
    selectedCategory && selectedMeta && !selectedMeta.active;

  const sliderProgress = Math.round(
    (filters.priceRange[1] / maxPrice) * 100
  );

  return (
    <div className="products-page page-transition">
      <Buy3Banner />

      <div className="products-header">
        <div className="container">
          <h1 className="page-title">Shop</h1>
          <p className="page-subtitle">T-shirts — pre-launch</p>
        </div>
      </div>

      <div className="container">
        <div className="products-container">
          
          {/* Sidebar */}
          <aside
            className={`products-sidebar ${
              showFilters ? 'open' : ''
            }`}
          >
            <div className="sidebar-header">
              <h2>Filters</h2>
              <button
                type="button"
                className="close-filters"
                onClick={() => setShowFilters(false)}
              >
                ×
              </button>
            </div>

            {/* Categories */}
            <div className="filter-section">
              <h3>Categories</h3>
              <div className="filter-options">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    {cat.active ? (
                      <label className="filter-option">
                        <input
                          type="radio"
                          name="category"
                          value={cat.id}
                          checked={
                            (queryCategory || filters.category) === cat.id ||
                            (!(queryCategory || filters.category) &&
                              cat.id === 't-shirts')
                          }
                          onChange={() =>
                            navigate(
                              `/products?category=${encodeURIComponent(cat.id)}`
                            )
                          }
                        />
                        <span className="filter-label">
                          {cat.name}
                        </span>
                      </label>
                    ) : (
                      <Link
                        className="filter-option filter-option--soon"
                        to={`/coming-soon?category=${encodeURIComponent(
                          cat.id
                        )}`}
                        onClick={() => setShowFilters(false)}
                      >
                        <span className="filter-label">
                          {cat.name}{' '}
                          <span className="coming-soon-pill">
                            Coming soon
                          </span>
                        </span>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sizes */}
            <div className="filter-section">
              <h3>Size</h3>
              <div className="size-options">
                {CONFIG.PRODUCT.SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`size-option ${
                      filters.size.includes(size)
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() =>
                      handleFilterChange('size', size)
                    }
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="filter-section">
              <h3>Price range</h3>
              <div className="price-range">
                <input
                  type="range"
                  min="0"
                  max={maxPrice}
                  value={filters.priceRange[1]}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      priceRange: [
                        prev.priceRange[0],
                        parseInt(e.target.value, 10),
                      ],
                    }))
                  }
                  className="price-slider"
                  style={{
                    '--range-progress': `${sliderProgress}%`,
                  }}
                />
                <div className="price-labels">
                  <span>₹0</span>
                  <span>₹{filters.priceRange[1]}</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="clear-filters"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </aside>

          {/* Main */}
          <main className="products-main">
            <div className="products-toolbar">
              <div className="toolbar-left">
                <button
                  type="button"
                  className="filter-toggle"
                  onClick={() =>
                    setShowFilters(!showFilters)
                  }
                >
                  <FiFilter /> Filters
                </button>
                <p className="product-count">
                  {products.length} products
                </p>
              </div>

              <div className="toolbar-right">
                <div className="view-toggle">
                  <button
                    type="button"
                    className={`view-btn ${
                      viewMode === 'grid' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('grid')}
                  >
                    <FiGrid />
                  </button>
                  <button
                    type="button"
                    className={`view-btn ${
                      viewMode === 'list' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('list')}
                  >
                    <FiList />
                  </button>
                </div>

                <div className="sort-dropdown">
                  <select
                    value={sortBy}
                    onChange={(e) =>
                      setSortBy(e.target.value)
                    }
                    className="sort-select"
                  >
                    <option value="newest">Newest</option>
                    <option value="name">Name</option>
                  </select>
                  <FiChevronDown className="dropdown-arrow" />
                </div>
              </div>
            </div>

            {/* States */}
            {showComingSoon ? (
              <div className="no-products">
                <p>This category is coming soon.</p>
                <Link
                  to="/products?category=t-shirts"
                  className="clear-filters-btn"
                >
                  Shop T-Shirts
                </Link>
              </div>
            ) : products.length === 0 ? (
              <div className="no-products">
                <p>No products found.</p>
                <button
                  type="button"
                  className="clear-filters-btn"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill,minmax(280px,1fr))',
                  gap: '32px',
                  width: '100%',
                }}
              >
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Products;